import { addResponseStatusToSpan } from '@vs-shop/engine/utils/httpTracing';
import { Effect } from 'effect';

import type { BackendApiErrorUnion } from '@vs-shop/engine/data/error/backendApi/_internal/createBackendApiError';
import { createBackendApiError } from '@vs-shop/engine/data/error/backendApi/_internal/createBackendApiError';
import { GlossaryAlphabet } from '@vs-shop/schemas/branded/MiscSchema';
import { schemaBodyJsonClean } from '@vs-shop/schemas/transform/schemaBodyJsonClean';
import { BackendApi } from '../../../service/api/BackendApi';
import { ApiModule } from '@vs-shop/engine/service/logger/ApiModule';
import { logApiOperation } from '@vs-shop/engine/utils/logger/apiLogger';
import { matchStatus } from 'effect/unstable/http/HttpClientResponse';

const ENDPOINT = '/api/v2/shop/glossary-terms/alphabet';
const HTTP_METHOD = 'get';

const ListGlossaryAlphabetError = createBackendApiError({
  domain: 'GlossaryTerm',
  endpoint: 'ListAlphabet',
  path: ENDPOINT,
  identifierField: 'page',
} as const);

export type GlossaryAlphabetError = BackendApiErrorUnion<typeof ListGlossaryAlphabetError>;

const listGlossaryTermsAlphabetFetch = Effect.fn(`http.${HTTP_METHOD} ${ENDPOINT}`, {
  kind: 'client',
  attributes: {
    'http.request.method': HTTP_METHOD,
  },
})(function* () {
  const apiClient = yield* BackendApi;
  return yield* apiClient
    .path(ENDPOINT)
    .method(HTTP_METHOD)
    .accept('application/ld+json')
    .fetch({});
});

/**
 * The distinct first characters of the glossary term names available in the
 * request locale. Neither the casing nor the character set is part of the
 * contract: the entries are whatever the catalogue starts with, so a consumer
 * must fold case when matching them.
 *
 * These values are the ones to filter by, verbatim. The endpoint groups on
 * `LOWER(SUBSTRING(name,1,1))` and the `?alphabet=` filter compares against
 * that same expression, so a reported character round-trips exactly. It also
 * means native initials are listings of their own — PostgreSQL compares under
 * a deterministic collation, where `č` is not `c` — so a consumer that renders
 * only a fixed `a–z` grid strands every term filed under one. rcprofi widens
 * its grid by the reported initials (`buildGlossaryAlphabetTiles`) rather than
 * dropping them.
 */
export const listGlossaryTermsAlphabet = listGlossaryTermsAlphabetFetch().pipe(
  Effect.map((response) => response.response),
  Effect.tap(addResponseStatusToSpan),
  Effect.withSpan('ValidateResponse'),
  Effect.flatMap(
    matchStatus({
      '2xx': (response) => schemaBodyJsonClean(GlossaryAlphabet)(response),
      404: (response) =>
        Effect.fail(new ListGlossaryAlphabetError.NotFoundError({ page: 1, response })),
      orElse: (response) =>
        Effect.fail(new ListGlossaryAlphabetError.ApiRequestError({ page: 1, response })),
    })
  ),
  Effect.tap((result) =>
    logApiOperation(
      ApiModule.GlossaryAlphabetGet,
      'Debug',
      `Successfully resolved glossary alphabet with ${String(result.alphabet.length)} entries`
    )
  ),
  Effect.withSpan('api.glossaryAlphabet.get', {
    kind: 'internal',
  })
);
